// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001R1 — PURE Ableitung des Fahrzeug-Konzessionsstatus (Partner-Portal).
//
// Vertrag R1: GET /partners/vehicles liefert je Fahrzeug ROHE Dokumentzeilen
//   (owner_type 'vehicle', document_type 'konzession') mit
//   { id, status, revision, is_effective, expires_at, issue_date, rejection_reason, created_at }.
// Die Fahrzeug-Konzession ist OPERATIV Pflicht: Fahrzeugfreigabe braucht eine wirksame
// Konzession; abgelaufene/abgelehnte Konzession SPERRT neue Fahrten. Das Portal ENTSCHEIDET
// nichts (Admin entscheidet via PATCH /api/admin/vehicles/:id/approve|reject) — es SPIEGELT nur.
//
// Diese Ableitung spiegelt die Backend-Logik (lib/documentLifecycle.itemStatus) rein clientseitig:
//   • effective = freigegebene, wirksame Revision (status='approved' & is_effective≠0; Legacy
//     is_effective=null gilt als wirksam);
//   • Ablauf wird DYNAMISCH ausgewertet (wie das Gate: am Ablauftag noch gültig, danach expired);
//   • renewalPending = gültige Freigabe + neuere, noch offene Einreichung (nicht gesperrt).
// Alles deterministisch (fester now-Anker möglich) und ohne Seiteneffekte.
// ─────────────────────────────────────────────────────────────────────────────

import { tagSchluessel } from './zeit';

const KONZESSION = 'konzession';

// YYYY-MM-DD eines Datums/Datumstrings (nur der Tagesanteil zählt für den Ablauf).
const dayPart = (v) => (v ? String(v).slice(0, 10) : null);
// ZEIT-HELFER-001: „heute" ist der BERLINER Kalendertag (lib/zeit.tagSchluessel), nicht der
// UTC-Tag. Zwischen 00:00 und 02:00 Berlin lag der UTC-Tag sonst noch auf gestern — eine am
// Vortag abgelaufene Konzession galt in dem Fenster fälschlich als noch gültig.
const todayPart = (now) => tagSchluessel(now || new Date());

// Abgelaufen = Ablauftag liegt VOR heute (am Ablauftag selbst noch gültig — wie expiryWatch).
const isExpiredDate = (expiresAt, now) => {
  const d = dayPart(expiresAt);
  if (!d) return false;
  return d < todayPart(now);
};

// Wirksam-freigegebene Revision: status approved UND is_effective≠0 (Legacy null gilt als wirksam).
const isEffectiveRow = (r) => r.status === 'approved' && (r.is_effective == null || Number(r.is_effective) === 1);

// Deterministische Ordnung wie im Backend: höchste Revision zuerst, dann jüngstes created_at,
// dann id (letzter Anker). COALESCE(revision,1) für Legacy-Zeilen ohne Revision.
const byRevisionDesc = (a, b) =>
  (Number(b.revision || 1) - Number(a.revision || 1)) ||
  String(b.created_at || '').localeCompare(String(a.created_at || '')) ||
  String(b.id || '').localeCompare(String(a.id || ''));

// Darstellung je Zustand (CI-Farbsystem: Grau/Violett + Rot für Fehler).
const PRESENTATION = {
  missing:        { label: 'Konzession fehlt',        color: '#6b7280', bg: '#f3f4f6' },
  pending:        { label: 'In Prüfung',              color: '#6b7280', bg: '#f3f4f6' },
  renewalPending: { label: 'Erneuerung in Prüfung',   color: '#6b7280', bg: '#f3f4f6' },
  approved:          { label: 'Genehmigt',             color: '#9C27B0', bg: '#faf5fb' },
  rejected:          { label: 'Abgelehnt',             color: '#dc2626', bg: '#fef2f2' },
  expired:           { label: 'Abgelaufen',            color: '#dc2626', bg: '#fef2f2' },
  compliance_locked: { label: 'Vom Support gesperrt',  color: '#dc2626', bg: '#fef2f2' },
};
export const konzessionPresentation = (state) => PRESENTATION[state] || PRESENTATION.missing;

// FAHRZEUG-ZULASSUNG-001: Die Zulassungsbescheinigung nutzt denselben Revisions-/
// Review-Lebenszyklus wie die Konzession, hat aber fachlich KEIN Ablaufdatum. Eigene
// Labels verhindern, dass der Partner den zweiten Pflichtpunkt mit der Konzession
// verwechselt. Eine operative Sperrwirkung wird hier bewusst NICHT behauptet: deren
// Aktivierung bleibt ein separates Owner-Gate im Backend.
const ZULASSUNG_PRESENTATION = {
  missing:           { label: 'Zulassungsschein fehlt', color: '#6b7280', bg: '#f3f4f6' },
  pending:           { label: 'In Prüfung', color: '#6b7280', bg: '#f3f4f6' },
  renewalPending:    { label: 'Aktualisierung in Prüfung', color: '#6b7280', bg: '#f3f4f6' },
  approved:          { label: 'Freigegeben', color: '#9C27B0', bg: '#faf5fb' },
  rejected:          { label: 'Abgelehnt', color: '#dc2626', bg: '#fef2f2' },
  expired:           { label: 'Abgelaufen', color: '#dc2626', bg: '#fef2f2' },
  compliance_locked: { label: 'Vom Support gesperrt', color: '#dc2626', bg: '#fef2f2' },
};
export const zulassungPresentation = (state) => ZULASSUNG_PRESENTATION[state] || ZULASSUNG_PRESENTATION.missing;

// Erneuerung/Upload bleibt bei Ablauf/Ablehnung AUSDRÜCKLICH offen (müssen nachreichbar sein).
// KEIN Upload bei laufender Prüfung (pending/renewalPending) und bei Compliance-Sperre — eine
// Compliance-/Betrugssperre wird durch Reupload NICHT aufgehoben (Regel 8).
export const canRenewKonzession = (state) =>
  state !== 'pending' && state !== 'renewalPending' && state !== 'compliance_locked';

// Gleicher Reupload-Vertrag wie bei der Konzession: keine Doppel-Einreichung bei
// laufender Prüfung und kein Reupload als vermeintliches Entsperren eines Compliance-Locks.
export const canResubmitZulassung = (state) =>
  state !== 'pending' && state !== 'renewalPending' && state !== 'compliance_locked';

// Nicht ablaufender zweiter Fahrzeugdokument-Slot. Anders als deriveVehicleKonzession
// wertet diese Funktion expires_at absichtlich nie aus. effective/latest bleiben getrennt,
// damit eine neue Einreichung nicht fälschlich als bereits freigegeben erscheint.
export function deriveVehicleZulassung(vehicle) {
  const rows = (vehicle?.documents || [])
    .filter((d) => d && d.document_type === 'zulassung')
    .slice()
    .sort(byRevisionDesc);
  const latest = rows[0] || null;
  const effective = rows.find(isEffectiveRow) || null;
  const locked = rows.some((d) => Number(d.compliance_locked) === 1);

  let state;
  if (locked) {
    state = 'compliance_locked';
  } else if (effective) {
    const renewalOpen = !!(latest && latest.status === 'pending' && latest.id !== effective.id);
    state = renewalOpen ? 'renewalPending' : 'approved';
  } else if (latest && latest.status === 'expired') {
    // Defensiv für bereits persistierte Fremdzustände; der neue Slot selbst läuft nie ab.
    state = 'expired';
  } else if (latest && latest.status === 'pending') {
    state = 'pending';
  } else if (latest && latest.status === 'rejected') {
    state = 'rejected';
  } else {
    state = 'missing';
  }

  const renewalOpen = state === 'renewalPending';
  const rejectionReason = latest && latest.status === 'rejected' && !effective
    ? (latest.rejection_reason || null)
    : null;
  const source = effective || latest;

  return {
    state,
    presentation: zulassungPresentation(state),
    issuedAt: source ? (source.issued_at || source.issue_date || null) : null,
    rejectionReason,
    effective,
    latest,
    renewalOpen,
    blocked: false,
    blockNotice: null,
    canRenew: canResubmitZulassung(state),
  };
}

// Haupt-Ableitung. vehicle: { documents:[…rohe Zeilen…], konzession_expiry?, konzession_issue? }.
export function deriveVehicleKonzession(vehicle, opts = {}) {
  const now = opts.now;
  const v = vehicle || {};
  const rows = (v.documents || []).filter((d) => d && d.document_type === KONZESSION).slice().sort(byRevisionDesc);
  const latest = rows[0] || null;
  const effective = rows.find(isEffectiveRow) || null;

  // Compliance-Sperre (Regel 8) überstimmt ALLES — wie Backend documentLifecycle.itemStatus:
  // ein gesperrtes Fahrzeug ist nie „genehmigt", egal ob eine wirksame Revision existiert.
  const locked = rows.some((d) => Number(d.compliance_locked) === 1);

  let state;
  if (locked) {
    state = 'compliance_locked';
  } else if (effective && !isExpiredDate(effective.expires_at, now)) {
    // Gültige Freigabe. Offene NEUERE Einreichung (andere Revision) → Erneuerung in Prüfung.
    const renewalOpen = !!(latest && latest.status === 'pending' && latest.id !== effective.id);
    state = renewalOpen ? 'renewalPending' : 'approved';
  } else if (effective) {
    state = 'expired';                                   // wirksam, aber Datum überschritten (dynamisch)
  } else if (latest && latest.status === 'expired') {
    state = 'expired';                                   // Backend-Sweep hat schon expired gesetzt
  } else if (latest && latest.status === 'pending') {
    state = 'pending';
  } else if (latest && latest.status === 'rejected') {
    state = 'rejected';
  } else {
    state = 'missing';
  }

  const renewalOpen = state === 'renewalPending';
  const blocked = state === 'expired' || state === 'rejected' || state === 'compliance_locked';
  const rejectionReason = latest && latest.status === 'rejected' && !effective ? (latest.rejection_reason || null) : null;
  const expiresAt = (effective || latest ? (effective || latest).expires_at : null) || v.konzession_expiry || null;

  let blockNotice = null;
  if (state === 'compliance_locked') blockNotice = 'Fahrzeug vom Support gesperrt — keine neuen Fahrten. Bitte den Support kontaktieren (eine Sperre wird nicht durch erneutes Hochladen aufgehoben).';
  else if (state === 'expired') blockNotice = 'Konzession abgelaufen — dieses Fahrzeug erhält keine neuen Fahrten. Bitte erneuern.';
  else if (state === 'rejected') blockNotice = 'Konzession abgelehnt — dieses Fahrzeug erhält keine neuen Fahrten. Bitte neu einreichen.';

  return {
    state,
    presentation: konzessionPresentation(state),
    expiresAt,
    rejectionReason,
    effective,
    latest,
    renewalOpen,
    blocked,
    blockNotice,
    canRenew: canRenewKonzession(state),
  };
}
